import { PROMO_CYCLE_MS, promoDeadlineKey } from './promoBanner.constants';

/**
 * Resolves the current countdown deadline (epoch ms) for an account, persisting it in localStorage.
 *
 * Logic (FE-only, per-account, always-on):
 * - First visit (nothing stored): deadline = now + 14 days.
 * - Return visit, still valid: use the stored deadline as-is.
 * - Expired: roll forward by whole 14-day cycles from the stored anchor to the nearest
 *   future deadline (keeps the original rhythm instead of resetting to now + 14 days).
 *
 * Returns 0 for an invalid accountId (caller should not render the countdown).
 */
export function resolvePromoDeadline(accountId: number): number {
  if (!accountId || Number.isNaN(accountId)) {
    return 0;
  }

  const now = Date.now();
  const key = promoDeadlineKey(accountId);
  const stored = Number(localStorage.getItem(key));

  // First visit (missing / corrupt value) -> start a fresh 14-day cycle.
  if (!stored || Number.isNaN(stored)) {
    const deadline = now + PROMO_CYCLE_MS;
    localStorage.setItem(key, String(deadline));
    return deadline;
  }

  // Still within the current cycle.
  if (stored > now) {
    return stored;
  }

  // Expired -> roll forward by whole cycles to the nearest future deadline.
  const cyclesPassed = Math.ceil((now - stored) / PROMO_CYCLE_MS);
  const next = stored + cyclesPassed * PROMO_CYCLE_MS;
  localStorage.setItem(key, String(next));
  return next;
}
